// Müşteri yöneticisi (K13) — portal `/api/v1/client-admin/:clientId/*` ve danışman
// `/clients/:id/admins` sözleşmeleri (KPI M16 T20, Rev 2 E6/Δ1, DR10).
// Yöneticilik yalnız `client_admins` tablosundan okunur; `users.clientId` yetki VERMEZ.

import { z } from "zod";
import { PARTICIPANT_STATUSES } from "../constants/index.js";
import {
  ClientEntitySchema,
  KPI_ACCESS_ROLES,
  KPI_EFFECTIVE_CLIENT_ACCESS,
  KPI_NODE_KINDS,
} from "./kpi.js";

/** Portal kullanıcı listesi durumu (DR10): DAVET BEKLİYOR / AKTİF / SÜRESİ DOLDU / KAPALI. */
export const CLIENT_USER_STATUSES = ["invited", "active", "expired", "closed"] as const;
export type ClientUserStatus = (typeof CLIENT_USER_STATUSES)[number];

export const CLIENT_USER_STATUS_LABELS: Record<ClientUserStatus, string> = {
  invited: "DAVET BEKLİYOR",
  active: "AKTİF",
  expired: "SÜRESİ DOLDU",
  closed: "KAPALI",
};

/**
 * v2 (0063) — müşteri yönetim rolü (`client_admins.role`). "Kullanıcı" satırsız durumdur
 * (yönetici değil); etiketi yine buradan okunur. Yönetim rolü veri erişimi VERMEZ (Y1).
 */
export const CLIENT_ADMIN_ROLES = ["super_admin", "admin"] as const;
export type ClientAdminRole = (typeof CLIENT_ADMIN_ROLES)[number];
export const CLIENT_ADMIN_ROLE_LABELS: Record<ClientAdminRole | "user", string> = {
  super_admin: "Süper Admin",
  admin: "Admin",
  user: "Kullanıcı",
};
/** Admin kapsamı (`client_admins.scope`); NULL alan = o boyutta tam. Süper Admin'de scope NULL. */
export const ClientAdminScopeSchema = z.object({
  categories: z.array(z.string().uuid()).max(500).nullable(),
  entities: z.array(z.string().uuid()).max(500).nullable(),
});
export interface ClientAdminScope {
  categories: string[] | null;
  entities: string[] | null;
}

/**
 * Set erişimi özeti — kategori düğümü bazında etkin erişim (danışman ∩ yönetici daraltması).
 * Kapsamlı Admin görünümünde (Task 8, D3-4) kapsam DIŞI set girdisi hiç dönmez (ad/jeton/etiket yok —
 * yalnız `ClientUser.hiddenSetCount`); kapsam içi setin kapsam dışı anahtar/işletme/istisnaları
 * düşürülür, yalnız sayıları (`hidden*Count`) ve `outOfScope` işareti kalır.
 */
export const ClientUserSetAccessSchema = z.object({
  setId: z.string().uuid(),
  setName: z.string(),
  /** Eski gölge (`categories_allowed`) — NULL = daraltma yok. Yeni istemci `categoryAccess` okur. */
  categoriesAllowed: z.array(z.string().uuid()).nullable(),
  /** v2 kategori haritası (uzlaştırılmış); NULL = tam. Admin'de yalnız kapsam içi anahtarlar. */
  categoryAccess: z.record(z.string().uuid(), z.enum(["open", "readonly"])).nullable(),
  /** Yaprak işletme kapsamı; NULL = tüm işletmeler. Admin'de yalnız kapsam içi id'ler. */
  entitiesAllowed: z.array(z.string().uuid()).nullable(),
  /** Gösterge istisnaları; Admin'de yalnız kapsam içi göstergeler. */
  overrides: z.array(
    z.object({
      setIndicatorId: z.string().uuid(),
      access: z.enum(KPI_EFFECTIVE_CLIENT_ACCESS),
    }),
  ),
  /** Bu satırda görüntüleyen Admin'in kapsamı dışında kalan erişim var (Süper Admin/danışmanda hep false). */
  outOfScope: z.boolean(),
  hiddenCategoryCount: z.number().int().nonnegative(),
  hiddenEntityCount: z.number().int().nonnegative(),
  hiddenOverrideCount: z.number().int().nonnegative(),
  /** M17 — erişim rolü; `auditor`'da `edits` her zaman boş (açık kategoriler `views`'a düşer). */
  role: z.enum(KPI_ACCESS_ROLES),
  /**
   * İyimser erişim jetonu (`granted_at` µs, SQL'de üretilir) — PUT gövdesinde `expected[setId]` olarak
   * geri gönderilir; uyuşmazlık 409 `KPI_ACCESS_STALE` (başka yönetici bu arada kaydetti).
   */
  accessToken: z.string(),
  /** Kategori etiketleri (yalnız müşteriye görünenler: open/readonly; Admin'de kapsam içi). */
  edits: z.array(z.string()),
  views: z.array(z.string()),
});
export type ClientUserSetAccess = z.infer<typeof ClientUserSetAccessSchema>;

export const ClientUserProjectSchema = z.object({
  projectId: z.string().uuid(),
  label: z.string(),
  participantStatus: z.enum(PARTICIPANT_STATUSES),
});
export type ClientUserProject = z.infer<typeof ClientUserProjectSchema>;

export const ClientUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  fullName: z.string(),
  status: z.enum(CLIENT_USER_STATUSES),
  isAdmin: z.boolean(),
  /** Bu firmadaki aktif yönetim rolü; NULL = Kullanıcı (yönetici değil). */
  adminRole: z.enum(CLIENT_ADMIN_ROLES).nullable(),
  /** Görüntüleyen Admin'in kapsamı dışında erişimi var (ayrıntı gösterilmez — yalnız işaret/sayı). */
  outOfScopeAccess: z.boolean(),
  /** Admin görünümünde listeden tamamen düşen (kapsam dışı) set erişimi sayısı. */
  hiddenSetCount: z.number().int().nonnegative(),
  activationExpiresAt: z.string().nullable(),
  createdAt: z.string(),
  kpiAccess: z.array(ClientUserSetAccessSchema),
  projects: z.array(ClientUserProjectSchema),
});
export type ClientUser = z.infer<typeof ClientUserSchema>;

export const ClientUsersResponseSchema = z.object({
  client: z.object({ id: z.string().uuid(), name: z.string() }),
  /** Danışman (clients.createdBy) — listede DEĞİL, üstte bilgi bandı (DR10). */
  consultant: z.object({ fullName: z.string(), email: z.string() }).nullable(),
  /** Aktif (isActive) yönetici sayısı (Süper Admin + Admin). */
  activeAdminCount: z.number().int(),
  /** Aktif Süper Admin sayısı (`revoked_at IS NULL ∧ users.is_active`) — son Süper Admin kuralı. */
  activeSuperAdminCount: z.number().int(),
  users: z.array(ClientUserSchema),
});
export type ClientUsersResponse = z.infer<typeof ClientUsersResponseSchema>;

/** v2 kategori haritası gövdesi — `{ "<set düğümü metricId>": "open" | "readonly" }` (D3-1). */
export const KpiCategoryAccessMapSchema = z.record(z.string().uuid(), z.enum(["open", "readonly"]));

/** Gösterge düzeyi istisna (D3-2) — kategori seviyesinin yerine geçer (Y4). */
export const KpiIndicatorOverrideSchema = z.object({
  setIndicatorId: z.string().uuid(),
  access: z.enum(KPI_EFFECTIVE_CLIENT_ACCESS),
});

/**
 * Set erişimi grant'ı (v2, spec §7). `categoriesAllowed`: harita | metricId dizisi (bir sürüm
 * geçiş — `parseCategoryAccess` ile `open` haritasına çevrilir) | NULL (tam). `entitiesAllowed`
 * yok = KORU, NULL = tüm işletmeler. `overrides` yok = KORU, `[]` = (kapsam içini) temizle.
 * Danışman açıklığı her zaman tavandır (E2) — grant yalnız daraltır/ince ayar yapar.
 */
export const KpiAccessGrantSchema = z.object({
  setId: z.string().uuid(),
  categoriesAllowed: z.union([KpiCategoryAccessMapSchema, z.array(z.string().uuid())]).nullable(),
  entitiesAllowed: z.array(z.string().uuid()).max(500).nullable().optional(),
  overrides: z
    .array(KpiIndicatorOverrideSchema)
    .max(1000)
    .refine((list) => new Set(list.map((o) => o.setIndicatorId)).size === list.length, {
      message: "Aynı göstergeye birden çok istisna",
    })
    .optional(),
  /** M17 — verilmezse mevcut rol korunur, yeni satır `owner` (eski istemciler rolsüz gönderir). */
  role: z.enum(KPI_ACCESS_ROLES).optional(),
});
export type KpiAccessGrant = z.infer<typeof KpiAccessGrantSchema>;

/** Auth bağlamı (`AuthenticatedUser.adminClients`, `/auth/me`) — aktif yöneticilik üyeliği. */
export const ClientAdminMembershipSchema = z.object({
  clientId: z.string().uuid(),
  role: z.enum(CLIENT_ADMIN_ROLES),
  scope: ClientAdminScopeSchema.nullable(),
});
export type ClientAdminMembership = z.infer<typeof ClientAdminMembershipSchema>;

/** Grant listesi — set başına tek grant (aynı set iki kez → 422 `validation`). */
const KpiAccessGrantListSchema = z
  .array(KpiAccessGrantSchema)
  .max(50)
  .refine((list) => new Set(list.map((g) => g.setId)).size === list.length, {
    message: "Aynı veri seti için birden çok erişim",
  });

export const SetKpiAccessRequestSchema = z.object({
  grants: KpiAccessGrantListSchema,
  /**
   * İyimser kilit (opsiyonel — eski çekmece göndermez): setId → okunan `accessToken`, NULL = "satır
   * yok" beklenir. Kilit altında herhangi biri tutmazsa hiçbir şey yazılmaz → 409 `KPI_ACCESS_STALE`.
   */
  expected: z.record(z.string().uuid(), z.string().max(40).nullable()).optional(),
});
export type SetKpiAccessRequest = z.infer<typeof SetKpiAccessRequestSchema>;

export const InviteClientUserRequestSchema = z.object({
  email: z.string().email(),
  fullName: z.string().min(1).max(200),
  /** Davetle birlikte set erişimi (isteğe bağlı) — mevcut kullanıcıyı listede görünür kılar. */
  grants: KpiAccessGrantListSchema.optional(),
});
export type InviteClientUserRequest = z.infer<typeof InviteClientUserRequestSchema>;

export const InviteClientUserResponseSchema = z.object({
  userId: z.string().uuid(),
  userCreated: z.boolean(),
  mailSent: z.boolean(),
  /** Mevcut aktif kullanıcı: erişim verildi, davet maili gönderilmedi (Finar 9.1). */
  existingActive: z.boolean().default(false),
});
export type InviteClientUserResponse = z.infer<typeof InviteClientUserResponseSchema>;

/** GET /client-admin/:clientId/sets — kategori daraltma kutuları için set + kategori düğümleri. */
export const ClientAdminSetCategorySchema = z.object({
  indicatorId: z.string().uuid(),
  metricId: z.string().uuid(),
  label: z.string(),
  depth: z.number().int(),
  /** Danışmanın belirlediği etkin açıklık (yönetici bunu aşamaz). */
  consultantAccess: z.enum(KPI_EFFECTIVE_CLIENT_ACCESS),
});
export type ClientAdminSetCategory = z.infer<typeof ClientAdminSetCategorySchema>;

/** Set gösterge ağacı düğümü (erişim çekmecesi — §8.1); Admin'de kapsam dışı düğüm yok. */
export const ClientAdminSetIndicatorSchema = z.object({
  id: z.string().uuid(),
  metricId: z.string().uuid(),
  /** Görünmeyen (kapsam dışı) ebeveyn NULL'a çekilir — kimliği sızmaz. */
  parentId: z.string().uuid().nullable(),
  label: z.string(),
  /** Görünür ağaçtaki derinlik (kırpılmış ağaçta kök 0). */
  depth: z.number().int().nonnegative(),
  hasChildren: z.boolean(),
  isDerived: z.boolean(),
  consultantAccess: z.enum(KPI_EFFECTIVE_CLIENT_ACCESS),
  /** Etkin formülün girdi göstergeleri (bu setin, görünür düğümleri). */
  derivedInputIds: z.array(z.string().uuid()),
  /**
   * Kırılım (Task 17): yalnız `category` / `breakdown_group` düğümünde yazılır (metrikte yok →
   * eski DTO byte-eşit). Çekmece toplam/üye ilişkisini shared `effectiveNodeKind` ile kurar.
   */
  nodeKind: z.enum(KPI_NODE_KINDS).optional(),
});
export type ClientAdminSetIndicator = z.infer<typeof ClientAdminSetIndicatorSchema>;

export const ClientAdminSetSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  reportScope: z.string(),
  categories: z.array(ClientAdminSetCategorySchema),
  indicators: z.array(ClientAdminSetIndicatorSchema),
  /** Görüntüleyen Admin'in kapsamı dışında kalan kategori düğümü sayısı (adları gönderilmez). */
  hiddenCategoryCount: z.number().int().nonnegative(),
});
export type ClientAdminSet = z.infer<typeof ClientAdminSetSchema>;

/** GET /client-admin/:clientId/sets — setler + firmanın işletmeleri (Admin'de kapsamla kırpılmış). */
export const ClientAdminSetsResponseSchema = z.object({
  sets: z.array(ClientAdminSetSchema),
  /** Admin'de yalnız kapsam yaprakları (gruplar ve kapsam dışı işletmeler yok, ebeveyn NULL). */
  entities: z.array(ClientEntitySchema),
  /** Görüntüleyen Admin'in kapsamı dışında kalan (yaprak) işletme sayısı. */
  hiddenEntityCount: z.number().int().nonnegative(),
});
export type ClientAdminSetsResponse = z.infer<typeof ClientAdminSetsResponseSchema>;

/** GET /client-admin/:clientId/projects — dar DTO (RK30 activeProjectFilter). */
export const ClientAdminProjectSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  frameworkName: z.string(),
  reportingYear: z.number().int(),
  status: z.string(),
});
export type ClientAdminProject = z.infer<typeof ClientAdminProjectSchema>;

export const ClientAdminFirmSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Aktörün bu firmadaki yönetim rolü + Admin kapsamı (Süper Admin'de NULL). */
  role: z.enum(CLIENT_ADMIN_ROLES),
  scope: ClientAdminScopeSchema.nullable(),
});
export type ClientAdminFirm = z.infer<typeof ClientAdminFirmSchema>;

// ─── Danışman tarafı: /clients/:id/admins ───────────────────────────────────

export const ClientAdminRowSchema = z.object({
  userId: z.string().uuid(),
  email: z.string().email(),
  fullName: z.string(),
  isActive: z.boolean(),
  activated: z.boolean(),
  role: z.enum(CLIENT_ADMIN_ROLES),
  /** Admin kapsamı; Süper Admin'de ve tam kapsamlı Admin'de NULL. */
  scope: ClientAdminScopeSchema.nullable(),
  grantedAt: z.string(),
  grantedByName: z.string().nullable(),
});
export type ClientAdminRow = z.infer<typeof ClientAdminRowSchema>;

/** Süper Admin'de kapsam olamaz (`client_admins` CHECK role='admin' OR scope IS NULL). */
function superAdminHasNoScope(v: { role?: ClientAdminRole | undefined; scope?: unknown }): boolean {
  return v.role !== "super_admin" || v.scope === undefined || v.scope === null;
}
const SUPER_SCOPE_MESSAGE = "Süper Admin'e kapsam verilemez";

/**
 * Yönetici ata (danışman `POST /clients/:id/admins`, Süper Admin `POST /client-admin/:clientId/admins`).
 * `role` ZORUNLU (R2-15 — DB varsayılanı yok). `scope` yalnız Admin'de (NULL/yok = tam).
 * `grantMatchingAccess`: kapsam içi setlerde kapsamıyla aynı set erişimi (Y5a birleştirmesiyle).
 */
export const GrantClientAdminRequestSchema = z
  .object({
    email: z.string().email(),
    fullName: z.string().min(1).max(200),
    role: z.enum(CLIENT_ADMIN_ROLES),
    scope: ClientAdminScopeSchema.nullable().optional(),
    grantMatchingAccess: z.boolean().optional(),
  })
  .refine(superAdminHasNoScope, { message: SUPER_SCOPE_MESSAGE, path: ["scope"] });
export type GrantClientAdminRequest = z.infer<typeof GrantClientAdminRequestSchema>;

/** Rol / kapsam değiştir (PATCH). `scope` yok = koru (Admin'de); Süper Admin'e geçişte silinir. */
export const UpdateClientAdminRequestSchema = z
  .object({
    role: z.enum(CLIENT_ADMIN_ROLES).optional(),
    scope: ClientAdminScopeSchema.nullable().optional(),
  })
  .refine(superAdminHasNoScope, { message: SUPER_SCOPE_MESSAGE, path: ["scope"] });
export type UpdateClientAdminRequest = z.infer<typeof UpdateClientAdminRequestSchema>;

export const GrantClientAdminResponseSchema = z.object({
  admin: ClientAdminRowSchema,
  userCreated: z.boolean(),
  mailSent: z.boolean(),
  /**
   * `grantMatchingAccess` sonucu: erişimi yazılan setler + kişinin mevcut (kapsamdan geniş) erişimi
   * nedeniyle birleştirilemeyip dokunulmayan setler. İstenmediyse NULL.
   */
  matchingAccess: z
    .object({
      setIds: z.array(z.string().uuid()),
      skippedSetIds: z.array(z.string().uuid()),
      /** X1-m2: NULL harita görünmez anahtar yüzünden açık haritaya döndü (gelecek kökler kapalı). */
      materializedSetIds: z.array(z.string().uuid()).default([]),
    })
    .nullable(),
});
export type GrantClientAdminResponse = z.infer<typeof GrantClientAdminResponseSchema>;

// ─── Danışman tarafı: /clients/:id/kpi-users (TODO-KPI-19) ──────────────────

/** GET /clients/:id/kpi-users — firmanın müşteri kullanıcıları + setler (kategori tavanı) tek çağrı. */
export const ClientKpiUsersResponseSchema = z.object({
  client: z.object({ id: z.string().uuid(), name: z.string() }),
  users: z.array(ClientUserSchema),
  sets: z.array(ClientAdminSetSchema),
  entities: z.array(ClientEntitySchema),
});
export type ClientKpiUsersResponse = z.infer<typeof ClientKpiUsersResponseSchema>;
